import { z } from 'zod';

/**
 * Validated runtime configuration (blueprint §6.4).
 *
 * Read here, in the composition root, and **injected** into infrastructure
 * factories. §3.4 forbids `infrastructure/` from reaching back into `app/` for
 * configuration, which is what keeps the SDK layer testable and keeps a second
 * source of truth from appearing.
 *
 * [HARD] §7.7 — anything prefixed `VITE_` is embedded in the client bundle and
 * is public. The values below are Firebase's *publishable* client identifiers,
 * which Google documents as safe to expose. What protects the project is the
 * security rules and the API-key restrictions, not the secrecy of these strings.
 * A privileged credential MUST NOT be added here.
 */

const firebaseConfigSchema = z.object({
  apiKey: z.string().min(1, 'VITE_FIREBASE_API_KEY مفقود'),
  authDomain: z.string().min(1, 'VITE_FIREBASE_AUTH_DOMAIN مفقود'),
  projectId: z.string().min(1, 'VITE_FIREBASE_PROJECT_ID مفقود'),
  storageBucket: z.string().min(1, 'VITE_FIREBASE_STORAGE_BUCKET مفقود'),
  messagingSenderId: z.string().min(1, 'VITE_FIREBASE_MESSAGING_SENDER_ID مفقود'),
  appId: z.string().min(1, 'VITE_FIREBASE_APP_ID مفقود'),
});

/** Emulator wiring. Absent in production; present during local development. */
const emulatorConfigSchema = z.object({
  enabled: z.boolean(),
  host: z.string().min(1),
  authPort: z.number().int().positive(),
  firestorePort: z.number().int().positive(),
  storagePort: z.number().int().positive(),
});

export type FirebaseConfig = z.infer<typeof firebaseConfigSchema>;
export type EmulatorConfig = z.infer<typeof emulatorConfigSchema>;

/**
 * Structurally the `FirebaseInit` the infrastructure factory declares.
 *
 * Deliberately not imported from there and not exported to there: `app/` may
 * depend on `infrastructure/`, never the reverse (§3.2). Validation lives here
 * because reading the environment is a composition-root job; the *shape* is the
 * factory's contract.
 */
export interface AppConfig {
  readonly firebase: FirebaseConfig;
  readonly emulator: EmulatorConfig;
}

/**
 * Fails loudly at startup rather than producing a half-configured client that
 * throws somewhere unrelated later. A missing key is a deployment error, and
 * the earliest possible failure is the cheapest one to diagnose.
 */
export function loadAppConfig(env: Record<string, unknown>): AppConfig {
  const firebase = firebaseConfigSchema.safeParse({
    apiKey: env['VITE_FIREBASE_API_KEY'],
    authDomain: env['VITE_FIREBASE_AUTH_DOMAIN'],
    projectId: env['VITE_FIREBASE_PROJECT_ID'],
    storageBucket: env['VITE_FIREBASE_STORAGE_BUCKET'],
    messagingSenderId: env['VITE_FIREBASE_MESSAGING_SENDER_ID'],
    appId: env['VITE_FIREBASE_APP_ID'],
  });

  if (!firebase.success) {
    // Names only. Printing the values would put publishable-but-still-project
    // -identifying strings into whatever collects console output (§7.12).
    const missing = firebase.error.issues.map((issue) => issue.path.join('.')).join(', ');
    throw new Error(`إعدادات Firebase غير مكتملة: ${missing}. راجع ملف .env.local`);
  }

  const emulatorEnabled = env['VITE_USE_FIREBASE_EMULATOR'] === 'true';

  return {
    firebase: firebase.data,
    emulator: emulatorConfigSchema.parse({
      enabled: emulatorEnabled,
      host: (env['VITE_EMULATOR_HOST'] as string | undefined) ?? '127.0.0.1',
      authPort: 9099,
      firestorePort: 8080,
      storagePort: 9199,
    }),
  };
}
